/**
 * Support Dashboard
 * Admin view of all support tickets with statistics
 */

import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../shared/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../shared/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../shared/components/ui/Select';
import { Button } from '../shared/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../shared/components/ui/dropdown-menu';
import { BarChart3, Calendar, Ticket, Loader2, Building2, Download, FileSpreadsheet, FileText, MessageCircle } from 'lucide-react';
import Navigation from './strategic_performance/components/Navigation';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import supportAPI, { SupportStats } from '../services/supportApi';
import { toast } from 'sonner';

const SupportDashboard = () => {
  const [stats, setStats] = useState<SupportStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [selectedCompany, setSelectedCompany] = useState<string>('all');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await supportAPI.getSupportStats();
        setStats(data);
      } catch (err: any) {
        console.error('Failed to fetch stats:', err);
        setError('Failed to load stats');
        toast.error('Failed to load support statistics');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const sortedMonths = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.byMonth).sort((a, b) => b[0].localeCompare(a[0]));
  }, [stats]);

  const sortedCompanies = useMemo(() => {
    if (!stats) return [];
    return [...stats.companies].sort((a, b) => a.localeCompare(b));
  }, [stats]);

  const filteredStats = useMemo(() => {
    if (!stats) return null;

    if (selectedMonth === 'all' && selectedCompany === 'all') {
      return stats;
    }

    if (selectedMonth !== 'all' && selectedCompany === 'all') {
      const monthData = stats.byMonth[selectedMonth];
      if (!monthData) return stats;

      const filteredByClient: Record<string, { tickets: number; replies: number }> = {};
      Object.entries(stats.byCompanyMonth).forEach(([company, months]) => {
        if (months[selectedMonth]) {
          filteredByClient[company] = months[selectedMonth];
        }
      });

      return {
        ...stats,
        totalTickets: monthData.tickets,
        totalReplies: monthData.replies,
        byClient: filteredByClient,
        byMonth: { [selectedMonth]: monthData },
      };
    }

    if (selectedMonth === 'all' && selectedCompany !== 'all') {
      const companyData = stats.byClient[selectedCompany];
      if (!companyData) return stats;

      const filteredByMonth: Record<string, { tickets: number; replies: number }> = {};
      const companyMonths = stats.byCompanyMonth[selectedCompany] || {};
      Object.entries(companyMonths).forEach(([month, data]) => {
        filteredByMonth[month] = data;
      });

      return {
        ...stats,
        totalTickets: companyData.tickets,
        totalReplies: companyData.replies,
        byClient: { [selectedCompany]: companyData },
        byMonth: filteredByMonth,
      };
    }

    const companyMonths = stats.byCompanyMonth[selectedCompany];
    if (!companyMonths || !companyMonths[selectedMonth]) {
      return {
        ...stats,
        totalTickets: 0,
        totalReplies: 0,
        byClient: {},
        byMonth: {},
      };
    }

    const data = companyMonths[selectedMonth];
    return {
      ...stats,
      totalTickets: data.tickets,
      totalReplies: data.replies,
      byClient: { [selectedCompany]: data },
      byMonth: { [selectedMonth]: data },
    };
  }, [stats, selectedMonth, selectedCompany]);

  const sortedClients = useMemo(() => {
    if (!filteredStats) return [];
    return Object.entries(filteredStats.byClient).sort((a, b) => b[1].tickets - a[1].tickets);
  }, [filteredStats]);

  const sortedFilteredMonths = useMemo(() => {
    if (!filteredStats) return [];
    return Object.entries(filteredStats.byMonth).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filteredStats]);

  const formatMonthLabel = (monthKey: string) => {
    const [year, m] = monthKey.split('-');
    return new Date(parseInt(year), parseInt(m) - 1).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  };

  const exportToExcel = () => {
    if (!filteredStats) return;

    const workbook = XLSX.utils.book_new();

    const summaryData = [
      ['Support Tickets Report'],
      [''],
      ['Filters Applied:'],
      ['Month', selectedMonth === 'all' ? 'All Time' : formatMonthLabel(selectedMonth)],
      ['Organization', selectedCompany === 'all' ? 'All Organizations' : selectedCompany],
      [''],
      ['Summary'],
      ['Total Tickets', filteredStats.totalTickets],
      ['Total Replies', filteredStats.totalReplies],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    const companyData = [['Organization', 'Tickets', 'Replies', 'Total']];
    Object.entries(filteredStats.byClient)
      .sort((a, b) => b[1].tickets - a[1].tickets)
      .forEach(([company, counts]) => {
        companyData.push([
          company,
          counts.tickets.toString(),
          counts.replies.toString(),
          (counts.tickets + counts.replies).toString(),
        ]);
      });
    const companySheet = XLSX.utils.aoa_to_sheet(companyData);
    XLSX.utils.book_append_sheet(workbook, companySheet, 'By Organization');

    const monthData = [['Month', 'Tickets', 'Replies', 'Total']];
    Object.entries(filteredStats.byMonth)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .forEach(([month, counts]) => {
        monthData.push([
          formatMonthLabel(month),
          counts.tickets.toString(),
          counts.replies.toString(),
          (counts.tickets + counts.replies).toString(),
        ]);
      });
    const monthSheet = XLSX.utils.aoa_to_sheet(monthData);
    XLSX.utils.book_append_sheet(workbook, monthSheet, 'By Month');

    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `support-stats-${timestamp}.xlsx`);
  };

  const exportToPDF = () => {
    if (!filteredStats) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(18);
    doc.text('Support Tickets Report', pageWidth / 2, 20, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(100);
    const filterText = `Month: ${
      selectedMonth === 'all' ? 'All Time' : formatMonthLabel(selectedMonth)
    } | Organization: ${selectedCompany === 'all' ? 'All Organizations' : selectedCompany}`;
    doc.text(filterText, pageWidth / 2, 28, { align: 'center' });

    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text('Summary', 14, 40);

    autoTable(doc, {
      startY: 45,
      head: [['Metric', 'Value']],
      body: [
        ['Total Tickets', filteredStats.totalTickets.toString()],
        ['Total Replies', filteredStats.totalReplies.toString()],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      margin: { left: 14, right: 14 },
    });

    const companyStartY = (doc as any).lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.text('Tickets by Organization', 14, companyStartY);

    const companyRows = Object.entries(filteredStats.byClient)
      .sort((a, b) => b[1].tickets - a[1].tickets)
      .map(([company, counts]) => [
        company,
        counts.tickets.toString(),
        counts.replies.toString(),
        (counts.tickets + counts.replies).toString(),
      ]);

    if (companyRows.length > 0) {
      autoTable(doc, {
        startY: companyStartY + 5,
        head: [['Organization', 'Tickets', 'Replies', 'Total']],
        body: companyRows,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 14, right: 14 },
      });
    }

    const monthStartY = (doc as any).lastAutoTable?.finalY + 15 || companyStartY + 20;

    if (monthStartY > 240) {
      doc.addPage();
      doc.setFontSize(14);
      doc.text('Tickets by Month', 14, 20);
    } else {
      doc.setFontSize(14);
      doc.text('Tickets by Month', 14, monthStartY);
    }

    const monthRows = Object.entries(filteredStats.byMonth)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([month, counts]) => [
        formatMonthLabel(month),
        counts.tickets.toString(),
        counts.replies.toString(),
        (counts.tickets + counts.replies).toString(),
      ]);

    if (monthRows.length > 0) {
      autoTable(doc, {
        startY: monthStartY > 240 ? 25 : monthStartY + 5,
        head: [['Month', 'Tickets', 'Replies', 'Total']],
        body: monthRows,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 14, right: 14 },
      });
    }

    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Generated on ${new Date().toLocaleString()} | Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    const timestamp = new Date().toISOString().split('T')[0];
    doc.save(`support-stats-${timestamp}.pdf`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error || !filteredStats) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="p-4 text-center text-muted-foreground">{error || 'No stats available'}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="container mx-auto px-4 py-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Support Dashboard</h1>
          <p className="text-muted-foreground">Monitor support tickets across all organizations</p>
        </div>

        <div className="space-y-6">
          {/* Filters and Export */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    {sortedMonths.map(([month]) => (
                      <SelectItem key={month} value={month}>
                        {formatMonthLabel(month)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {sortedCompanies.map((company) => (
                      <SelectItem key={company} value={company}>
                        {company}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer">
                  <FileSpreadsheet className="h-4 w-4" />
                  Export to Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer">
                  <FileText className="h-4 w-4" />
                  Export to PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
                <Ticket className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredStats.totalTickets}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Replies</CardTitle>
                <MessageCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredStats.totalReplies}</div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Stats */}
          <Tabs defaultValue="clients" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="clients" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                By Organization
              </TabsTrigger>
              <TabsTrigger value="months" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                By Month
              </TabsTrigger>
            </TabsList>

            <TabsContent value="clients" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Tickets by Organization
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sortedClients.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No data yet</p>
                  ) : (
                    <div className="space-y-4">
                      {sortedClients.map(([client, counts]) => {
                        const total = counts.tickets + counts.replies;
                        return (
                          <div key={client} className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-sm">{client}</span>
                              <span className="text-xs text-muted-foreground">
                                {counts.tickets} tickets, {counts.replies} replies
                              </span>
                            </div>
                            <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                              {counts.tickets > 0 && (
                                <div
                                  className="bg-primary"
                                  style={{ width: `${(counts.tickets / total) * 100}%` }}
                                />
                              )}
                              {counts.replies > 0 && (
                                <div
                                  className="bg-chart-2"
                                  style={{ width: `${(counts.replies / total) * 100}%` }}
                                />
                              )}
                            </div>
                            <div className="flex gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <div className="h-2 w-2 rounded-full bg-primary" />
                                {counts.tickets} tickets
                              </span>
                              <span className="flex items-center gap-1">
                                <div className="h-2 w-2 rounded-full bg-chart-2" />
                                {counts.replies} replies
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="months" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Tickets by Month
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sortedFilteredMonths.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No data yet</p>
                  ) : (
                    <div className="space-y-4">
                      {sortedFilteredMonths.map(([month, counts]) => {
                        const total = counts.tickets + counts.replies;
                        return (
                          <div key={month} className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-sm">{formatMonthLabel(month)}</span>
                              <span className="text-xs text-muted-foreground">
                                {counts.tickets} tickets, {counts.replies} replies
                              </span>
                            </div>
                            <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                              {counts.tickets > 0 && (
                                <div
                                  className="bg-primary"
                                  style={{ width: `${(counts.tickets / total) * 100}%` }}
                                />
                              )}
                              {counts.replies > 0 && (
                                <div
                                  className="bg-chart-2"
                                  style={{ width: `${(counts.replies / total) * 100}%` }}
                                />
                              )}
                            </div>
                            <div className="flex gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <div className="h-2 w-2 rounded-full bg-primary" />
                                {counts.tickets} tickets
                              </span>
                              <span className="flex items-center gap-1">
                                <div className="h-2 w-2 rounded-full bg-chart-2" />
                                {counts.replies} replies
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default SupportDashboard;
